import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '@env';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subject, take, takeUntil } from 'rxjs';

import { AssetService } from '../../models/assetservice.class';
import { Entity } from '../../models/entity.class';
import { Field } from '../../models/field.class';
import { Record } from '../../models/record/record.class';
import { RecordField } from '../../models/record/recordfield.class';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { GetAppConfigurator } from '../../store/actions/apps.actions';
import { getAppConfiguratorsMap$ } from '../../store/selectors/apps.selector';
import {
    buildModelFieldExistenceChecker,
    extractFieldVisibilityRules,
    FieldExistenceChecker,
    FieldValueResolver,
    FieldVisibilityRulesMap,
    isFieldVisibleByRule,
} from '../../utils/field-visibility.util';
import { FieldLevelFileAttachmentComponent } from '../field-level-file-attachment/field-level-file-attachment.component';

@Component({
    selector: 'lib-recordfields',
    templateUrl: './recordfields.component.html',
    styleUrls: ['./recordfields.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class RecordFieldsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() entity: Entity;
    @Input() record: Record;
    @Input() instanceId: string;
    @Input() showGroups: boolean;
    @Input() service: AssetService;
    @Input() inputDisabled: boolean;
    @Input() fieldsToHighlight: string[] = [];
    @Input() readOnly: boolean;
    @Input() isEntityReadonly: boolean;
    @Input() masterEntityFieldValueMap: { [property: string]: string };
    @Input() referencedFieldValueMap: { [property: string]: any };
    @Input() fieldsMap: BehaviorSubject<{
        [property: string]: {
            keyColumns: string[];
            valuesMap: { id: string; entityId: string; masterColumn: string; value: any; references: any[] }[];
        };
    }>;
    @Input() inAuditTrailMode = false;
    @Input() hasEditPermission: boolean;
    @Input() recordUpdateDisabled: boolean;
    @Input() showOnlyBusinessKeyFields: boolean;
    @Input() recordIds: string[];
    @Input() parentRecordId: string;
    @Input() assetMetaData: any;

    @Output() recordUpdated = new EventEmitter();
    @Output() getRecord = new EventEmitter();
    @Output() editRecord = new EventEmitter();
    @Output() recordInputModelChange = new EventEmitter();

    checkNaN = isNaN;
    currentEntity: string;
    groups: any[];
    isRecordDefined: string;
    definedFields: RecordField[];
    readOnlyGroups: any[];
    attachmentUri = environment.taxilla_api + '/asset/attachments?fileUri=';

    private fieldVisibilityRules: FieldVisibilityRulesMap = {};
    private modelFieldExists: FieldExistenceChecker;
    private destroy$ = new Subject<void>();

    constructor(private dialog: MatDialog, public sanitizer: DomSanitizer, private store$: Store) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.record || changes.readOnly) {
            this.record?.fields && this.getAllGroups(this.record.fields);
        }
        if (changes.service) {
            this.ensureVisibilityRulesLoaded();
        }
        if (changes.assetMetaData) {
            this.modelFieldExists = buildModelFieldExistenceChecker(this.assetMetaData);
            this.store$
                .select(getAppConfiguratorsMap$)
                .pipe(take(1))
                .subscribe(configuratorMap => this.refreshVisibilityRules(configuratorMap));
        }
    }

    private refreshVisibilityRules = (configuratorMap: any): void => {
        const serviceId = this.service?.serviceId;
        const assetUid = this.service?.assetMetaUId || this.assetMetaData?.uid;
        this.fieldVisibilityRules = serviceId && assetUid ? extractFieldVisibilityRules(configuratorMap?.[serviceId]?.[assetUid]) : {};
    };

    private ensureVisibilityRulesLoaded(): void {
        const serviceId = this.service?.serviceId;
        if (!serviceId) {
            return;
        }
        const assetUid = this.service?.assetMetaUId || this.assetMetaData?.uid;
        this.store$
            .select(getAppConfiguratorsMap$)
            .pipe(take(1))
            .subscribe(configuratorMap => {
                if (!assetUid || !configuratorMap?.[serviceId]?.[assetUid]) {
                    this.store$.dispatch(GetAppConfigurator({ serviceId, assetId: assetUid, noAlerts: true }));
                }
            });
    }

    private resolveFieldValue: FieldValueResolver = (entityUid, fieldUid) => {
        const local = this.record?.fields?.find(f => f.id === fieldUid && (!entityUid || f.entityUid === entityUid));
        if (local) {
            return local.value;
        }
        const key = entityUid + '^' + fieldUid;
        const referenced = this.referencedFieldValueMap?.[key];
        return referenced !== undefined ? referenced : this.masterEntityFieldValueMap?.[key];
    };

    private fieldExists: FieldExistenceChecker = (entityUid, fieldUid) => {
        if (!fieldUid) {
            return false;
        }
        if (this.modelFieldExists) {
            return this.modelFieldExists(entityUid, fieldUid);
        }
        const localExists = !!this.record?.fields?.some(f => f.id === fieldUid && (!entityUid || f.entityUid === entityUid));
        if (localExists) {
            return true;
        }
        const key = entityUid + '^' + fieldUid;
        return (
            (!!this.referencedFieldValueMap && Object.prototype.hasOwnProperty.call(this.referencedFieldValueMap, key)) ||
            (!!this.masterEntityFieldValueMap && Object.prototype.hasOwnProperty.call(this.masterEntityFieldValueMap, key))
        );
    };

    isFieldShown = (field: RecordField): boolean => {
        if (!field) {
            return false;
        }
        const rule = this.fieldVisibilityRules?.[field.id];
        field.conditionalShow = rule ? isFieldVisibleByRule(rule, this.resolveFieldValue, this.fieldExists) : true;
        return field.show !== false && field.conditionalShow;
    };

    getAllGroups = (fields: RecordField[]) => {
        this.groups = [];
        this.readOnlyGroups = [];
        this.definedFields = fields && fields.filter(field => field.value !== undefined);
        this.isRecordDefined = (this.record?.id || (this.record?.fields && this.record?.fields?.length > 0 ? 'true' : undefined)) as any;
        if (this.showGroups) {
            fields &&
                fields.forEach(field => {
                    if (field.show !== false && this.groups.indexOf(field.fieldSection) === -1) {
                        if (field.fieldSection) {
                            this.groups.push(field.fieldSection);
                        } else {
                            this.groups.unshift(field.fieldSection);
                        }
                    }
                    if (
                        field.datatype !== 'GRID' &&
                        !field.isBusinessKey &&
                        field.show !== false &&
                        this.readOnlyGroups.indexOf(field.fieldSection) === -1
                    ) {
                        if (field.fieldSection) {
                            this.readOnlyGroups.push(field.fieldSection);
                        } else {
                            this.readOnlyGroups.unshift(field.fieldSection);
                        }
                    } else if (
                        field.datatype === 'GRID' &&
                        !field.isBusinessKey &&
                        field.show !== false &&
                        this.readOnlyGroups.indexOf(field.fieldSection) === -1
                    ) {
                        if (field.fieldSection) {
                            this.readOnlyGroups.push(field.fieldSection);
                        } else {
                            this.readOnlyGroups.unshift(field.fieldSection);
                        }
                    } else if (
                        field.inAudit &&
                        !field.isBusinessKey &&
                        field.show !== false &&
                        this.readOnlyGroups.indexOf(field.fieldSection) === -1
                    ) {
                        if (field.fieldSection) {
                            this.readOnlyGroups.push(field.fieldSection);
                        } else {
                            this.readOnlyGroups.unshift(field.fieldSection);
                        }
                    }
                    if (this.record && this.record.message.errors && this.record.message.errors.length > 0) {
                        if (field && field.isMandatory && (field.value === '' || field.value === undefined || field.value === null)) {
                            const message = field.displayName + ' is mandatory';
                            if (field.message.errors.indexOf(message) === -1) {
                                field.message.errors.push(message);
                            }
                        }
                    }
                });
        }
    };

    trackByFieldId = (_index: number, field: RecordField) => {
        return field.id;
    };

    trackByGroup = (_index: number, section: string) => {
        return section;
    };

    trackByFile = (index: number) => {
        return index;
    };
    openAttachModal = (field: RecordField) => {
        const dialogRef = this.dialog.open(FieldLevelFileAttachmentComponent, {
            panelClass: ['fieldFileUploadModal', 'matDialogContainer'],
            disableClose: true,
        });
        dialogRef.componentInstance.service = this.service;
        dialogRef.componentInstance.record = this.record;
        dialogRef.componentInstance.field = field;
        dialogRef.componentInstance.instanceId = this.instanceId;
        dialogRef.componentInstance.recordUpdateDisabled = true;
        dialogRef.componentInstance.inReadOnlyMode = true;
        dialogRef.componentInstance.generateUploadedFiles();
    };

    inputModelChange = (input: string, record: Record, field: Field) => {
        this.recordInputModelChange.emit({ input, record, field });
    };

    decodeTextFieldValue = (value: any, returnString?: boolean) => {
        return CommonUtilsService.decodeTextFieldValue(value, this.sanitizer, returnString);
    };

    ngOnInit() {
        this.record?.fields && this.getAllGroups(this.record.fields);
        this.ensureVisibilityRulesLoaded();
        this.store$
            .select(getAppConfiguratorsMap$)
            .pipe(takeUntil(this.destroy$))
            .subscribe(configuratorMap => this.refreshVisibilityRules(configuratorMap));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
